/**
 * User and Permission Models
 * Aligned with backend Oath DTOs from Bss.ExternalTbond
 */

/**
 * @typedef {Object} OathPermission
 * @property {number} permissionTypeId - PortalUserPermissionType ID
 * @property {string} name - Display name
 * @property {string} slug - Permission slug for code checks
 * @property {string} category - Permission category
 * @property {boolean} [isInherited] - True if inherited from GA (General Agency) parent — read-only in admin UI
 */

/**
 * Permissions dictionary keyed by agencyId (as string).
 * Each key maps to the resolved permissions for that agency.
 * @typedef {Object<string, OathPermission[]>} PermissionsDict
 */

/**
 * @typedef {Object} OathUserAgency
 * @property {number} agencyId - Agency ID
 * @property {string} dbaAgencyName - DBA agency name
 * @property {string} legalAgencyName - Legal agency name
 * @property {boolean} canPost - Can post transfer bonds
 * @property {boolean} canRequest - Can request transfer bonds
 * @property {string} primaryContact - Primary contact name
 */

/**
 * @typedef {Object} OathUserInfo
 * @property {number} portalUserId - Primary user identifier
 * @property {string} loginId - Username
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} email - User's email address
 * @property {boolean} forcePasswordChange - Whether user must change password
 * @property {PermissionsDict} permissions - Dictionary of permissions keyed by agencyId
 * @property {OathUserAgency[]} agencies - Array of user's agencies with capabilities
 */

/**
 * @typedef {Object} OathLoginResponse
 * @property {string} token - JWT access token
 * @property {string} refreshToken - Refresh token for session extension
 * @property {OathUserInfo} user - User information with permissions
 */

/**
 * @typedef {Object} OathLoginRequest
 * @property {string} loginId - Username
 * @property {string} password - Plain text password
 * @property {string} timeZone - User's timezone (e.g., "America/New_York")
 */

/**
 * @typedef {Object} OathRefreshTokenRequest
 * @property {number} portalUserId - User ID
 * @property {string} refreshToken - Current refresh token
 * @property {string} timeZone - User's timezone
 */

/**
 * Local storage keys for Oath authentication
 */
export const OathStorageKeys = {
  JWT_TOKEN: 'oathJwtToken',
  REFRESH_TOKEN: 'oathRefreshToken',
  PORTAL_USER_ID: 'oathPortalUserId',
  SESSION_EXPIRY: 'oathSessionExpiry',
  USER_INFO: 'oathUserInfo',
};

/**
 * Session configuration
 */
export const SessionConfig = {
  SESSION_DURATION_HOURS: 4,
  WARNING_THRESHOLD_MINUTES: [60, 30, 10], // Show warnings at these intervals
};

/**
 * Helper to check if user has a specific permission
 * @param {OathPermission[]} permissions - User's permissions array
 * @param {string} slug - Permission slug to check
 * @returns {boolean}
 */
export const hasPermission = (permissions, slug) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.some((p) => p.slug === slug);
};

/**
 * Helper to check if user has any of the specified permissions
 * @param {OathPermission[]} permissions - User's permissions array
 * @param {string[]} slugs - Array of permission slugs to check
 * @returns {boolean}
 */
export const hasAnyPermission = (permissions, slugs) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return slugs.some((slug) => hasPermission(permissions, slug));
};

/**
 * Helper to check if user has all of the specified permissions
 * @param {OathPermission[]} permissions - User's permissions array
 * @param {string[]} slugs - Array of permission slugs to check
 * @returns {boolean}
 */
export const hasAllPermissions = (permissions, slugs) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return slugs.every((slug) => hasPermission(permissions, slug));
};

/**
 * Helper to get permissions by category
 * @param {OathPermission[]} permissions - User's permissions array
 * @param {string} category - Category to filter by
 * @returns {OathPermission[]}
 */
export const getPermissionsByCategory = (permissions, category) => {
  if (!permissions || !Array.isArray(permissions)) return [];
  return permissions.filter((p) => p.category === category);
};

// ============================================
// Per-Agency Permission Helpers
// ============================================

/**
 * Normalize permissions into a dictionary keyed by agencyId.
 * If the backend returns a legacy flat array, spreads it across all user agency IDs.
 * If already a dict, returns as-is.
 * @param {OathPermission[]|PermissionsDict} permissions - Raw permissions from API
 * @param {OathUserAgency[]} [agencies] - User's agencies (used for legacy array conversion)
 * @returns {PermissionsDict}
 */
export const normalizePermissions = (permissions, agencies) => {
  if (!permissions) return {};

  // Already a dict — return as-is
  if (!Array.isArray(permissions)) {
    return permissions;
  }

  // Legacy flat array — spread across all agency IDs
  if (agencies && agencies.length > 0) {
    const dict = {};
    for (const agency of agencies) {
      dict[String(agency.agencyId)] = permissions;
    }
    return dict;
  }

  // Flat array but no agencies — use fallback key
  return { '0': permissions };
};

/**
 * Flatten a permissions dictionary into a deduplicated array.
 * Deduplicates by slug (first occurrence wins).
 * @param {PermissionsDict} permissionsDict
 * @returns {OathPermission[]}
 */
export const flattenPermissions = (permissionsDict) => {
  if (!permissionsDict || typeof permissionsDict !== 'object') return [];

  const seen = new Set();
  const result = [];

  for (const perms of Object.values(permissionsDict)) {
    if (!Array.isArray(perms)) continue;
    for (const p of perms) {
      if (p.slug && !seen.has(p.slug)) {
        seen.add(p.slug);
        result.push(p);
      }
    }
  }

  return result;
};

/**
 * Get permissions for a specific agency from the dictionary.
 * Handles string/number agencyId coercion.
 * @param {PermissionsDict} permissionsDict
 * @param {string|number} agencyId
 * @returns {OathPermission[]}
 */
export const getAgencyPermissions = (permissionsDict, agencyId) => {
  if (!permissionsDict || !agencyId) return [];
  return permissionsDict[String(agencyId)] || [];
};

/**
 * Check if ANY agency has a specific permission slug.
 * @param {PermissionsDict} permissionsDict
 * @param {string} slug
 * @returns {boolean}
 */
export const hasPermissionInAnyAgency = (permissionsDict, slug) => {
  if (!permissionsDict || typeof permissionsDict !== 'object') return false;

  for (const perms of Object.values(permissionsDict)) {
    if (Array.isArray(perms) && perms.some((p) => p.slug === slug)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if ANY agency has any of the specified permission slugs.
 * @param {PermissionsDict} permissionsDict
 * @param {string[]} slugs
 * @returns {boolean}
 */
export const hasAnyPermissionInAnyAgency = (permissionsDict, slugs) => {
  if (!permissionsDict || typeof permissionsDict !== 'object') return false;
  return slugs.some((slug) => hasPermissionInAnyAgency(permissionsDict, slug));
};

/**
 * Check if each slug exists in at least one agency.
 * NOTE: Slugs can be satisfied by DIFFERENT agencies
 * (slug A in agency 1, slug B in agency 2 = pass).
 * @param {PermissionsDict} permissionsDict
 * @param {string[]} slugs
 * @returns {boolean}
 */
export const hasAllPermissionsAcrossAgencies = (permissionsDict, slugs) => {
  if (!permissionsDict || typeof permissionsDict !== 'object') return false;
  return slugs.every((slug) => hasPermissionInAnyAgency(permissionsDict, slug));
};

/**
 * Helper to check if user has any agency that can request transfer bonds
 * @deprecated Use permission slugs instead (CREATE:TRANSFER-BOND:REQUEST)
 * @param {OathUserAgency[]} agencies - User's agencies array
 * @returns {boolean}
 */
export const canRequestTransferBonds = (agencies) => {
  if (!agencies || !Array.isArray(agencies)) return false;
  return agencies.some((a) => a.canRequest);
};

/**
 * Helper to check if user has any agency that can post transfer bonds
 * @deprecated Use permission slugs instead (UPDATE:TRANSFER-BOND:POST)
 * @param {OathUserAgency[]} agencies - User's agencies array
 * @returns {boolean}
 */
export const canPostTransferBonds = (agencies) => {
  if (!agencies || !Array.isArray(agencies)) return false;
  return agencies.some((a) => a.canPost);
};

/**
 * Helper to get agencies that can request transfer bonds
 * @deprecated Use permission slugs instead
 * @param {OathUserAgency[]} agencies - User's agencies array
 * @returns {OathUserAgency[]}
 */
export const getRequestingAgencies = (agencies) => {
  if (!agencies || !Array.isArray(agencies)) return [];
  return agencies.filter((a) => a.canRequest);
};

/**
 * Helper to get agencies that can post transfer bonds
 * @deprecated Use permission slugs instead
 * @param {OathUserAgency[]} agencies - User's agencies array
 * @returns {OathUserAgency[]}
 */
export const getPostingAgencies = (agencies) => {
  if (!agencies || !Array.isArray(agencies)) return [];
  return agencies.filter((a) => a.canPost);
};
